/**
 * Timeline-specific constants for the timeline editor component.
 * These are UI/layout constants, not timing constants (those are in lib/constants.ts).
 */

import { MIN_TIMELINE_PIXELS_PER_SECOND } from '@/lib/constants'

/** Default height of a track in pixels */
export const DEFAULT_TRACK_HEIGHT = 46

/** Minimum height a track can be resized to */
export const MIN_TRACK_HEIGHT = 24

/** Maximum height a track can be resized to */
export const MAX_TRACK_HEIGHT = 200

/** Height of the time ruler at the top of the timeline */
export const RULER_HEIGHT = 32

/** Width of the track header (lock button, track label) */
export const TRACK_HEADER_WIDTH = 180

export { MIN_TIMELINE_PIXELS_PER_SECOND }

/** Maximum zoom level (pixels per second) */
export const MAX_PIXELS_PER_SECOND = 1500

/** Zoom step for toolbar +/- buttons (pixels per second per click) */
export const ZOOM_STEP = 50

/** Empty space kept after the last clip, in seconds, so there is always room to drop onto. */
export const MIN_TIMELINE_TAIL_SECONDS = 5

/** Share of the visible timeline width kept empty past the last clip. */
export const TIMELINE_TAIL_VIEWPORT_FRACTION = 0.6

/**
 * Seconds of empty timeline to keep after the last clip at the current zoom: at
 * least MIN_TIMELINE_TAIL_SECONDS, or TIMELINE_TAIL_VIEWPORT_FRACTION of the
 * viewport, whichever is longer. Zooming out shrinks a fixed 5 s tail to nothing
 * on screen; sizing it from the viewport keeps the drop room constant.
 */
export function getTimelineTailSeconds(containerWidth: number, pixelsPerSecond: number): number {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(pixelsPerSecond) ||
    containerWidth <= 0 ||
    pixelsPerSecond <= 0
  ) {
    return MIN_TIMELINE_TAIL_SECONDS
  }
  return Math.max(MIN_TIMELINE_TAIL_SECONDS, (containerWidth * TIMELINE_TAIL_VIEWPORT_FRACTION) / pixelsPerSecond)
}

/** Calculate the minimum zoom that fits the full sequence with 10% horizontal padding. */
export function getTimelineMinimumPixelsPerSecond(containerWidth: number, totalSeconds: number): number {
  if (!Number.isFinite(containerWidth) || !Number.isFinite(totalSeconds) || containerWidth <= 0 || totalSeconds <= 0) {
    return MIN_TIMELINE_PIXELS_PER_SECOND
  }

  const fitZoom = (containerWidth * 0.9) / totalSeconds
  return Math.max(MIN_TIMELINE_PIXELS_PER_SECOND, Math.min(fitZoom, MAX_PIXELS_PER_SECOND))
}

/**
 * Calculate a clip's rendered width in pixels.
 * The 3px floor keeps one-frame clips selectable; densely packed clips may
 * overlap slightly at overview zoom and separate as the user zooms in.
 */
export function getTimelineClipRenderWidth(durationSeconds: number, pixelsPerSecond: number): number {
  return Math.max(durationSeconds * pixelsPerSecond, 3)
}

/**
 * Two 8px trim handles need 8px of uncovered clip body between them.
 * Narrower clips stay body-only until the user zooms in.
 */
export function shouldShowTimelineClipTrimHandles(widthPx: number): boolean {
  return widthPx >= 24
}

/** Height of the video/audio section divider */
export const SECTION_DIVIDER_HEIGHT = 2

/** Minimum height for video or audio section */
export const MIN_SECTION_HEIGHT = 80

/** Default number of tracks when timeline initializes */
export { DEFAULT_TRACK_COUNT } from '@/lib/constants'

/** Maximum number of tracks allowed */
export { MAX_TRACK_COUNT } from '@/lib/constants'

/**
 * Sentinel track indices for drag-to-create track feature.
 * These indicate the user is dragging beyond existing tracks and should create a new one.
 */
export const NEW_VIDEO_TRACK_SENTINEL = -1
export const NEW_AUDIO_TRACK_SENTINEL = -2

/**
 * Sentinel indicating invalid track position (clip type doesn't match track type).
 * When this is the preview track, the ghost should be hidden.
 */
export const INVALID_TRACK_SENTINEL = -999

/** Check if a track index is a "new track" sentinel */
export function isNewTrackSentinel(trackIndex: number): boolean {
  return trackIndex === NEW_VIDEO_TRACK_SENTINEL || trackIndex === NEW_AUDIO_TRACK_SENTINEL
}

/** Check if a track index indicates an invalid position (ghost should be hidden) */
export function isInvalidTrack(trackIndex: number): boolean {
  return trackIndex === INVALID_TRACK_SENTINEL
}

/** Get the track type for a sentinel value */
export function getNewTrackType(trackIndex: number): 'video' | 'audio' | null {
  if (trackIndex === NEW_VIDEO_TRACK_SENTINEL) return 'video'
  if (trackIndex === NEW_AUDIO_TRACK_SENTINEL) return 'audio'
  return null
}
